import React, { useMemo } from "react";
import { DOC_TYPES, VOLUME_TIERS, LIDAR_MATRIX, LIDAR_MODEL, DRONE_MODEL, ARRAY_SHEET, RATE_CATALOG } from "../../../lib/constants.js";
import { fmtMoney, fmtDate } from "../../../lib/utils.js";
import { scheduleLabel as readScheduleLabel } from "../../../lib/quoteSchedule.js";
import { PRO_BONO_PRINT_NOTE } from "../../../lib/proBono.js";
import { ESTIMATE_NOTE, wordingOr } from "../../../lib/printWording.js";
import { proBonoPrints, calcLineDiscount, calcTotals, quoteDiscountRow, shownDiscountAmounts, buildDeliverablesText, buildTermsText, buildPoliciesText, addendumImages, hasLidarLineItem, lidarSheetEquipment, lidarProcessingOnly, lidarModelVersion, hasDroneLineItem, droneProcessingOnly, isSectionOn, showsArraySheet, groupLineItems, quoteLineGroups, lineGroupNotesFrom, turnaroundLines, ARRAY_SHEET_FOOT, lidarSheetNote, lidarSheetNoteV1, droneSheetNote } from "../../../lib/builderHelpers.js";
import logoBlack from "../../../assets/nyc-lockup-horizontal-black.png";
import rtcImg from "../../../assets/equipment/rtc.jpg";
import r5Img from "../../../assets/equipment/r5.jpg";
import bigRigImg from "../../../assets/equipment/big_rig.jpg";
import headRigImg from "../../../assets/equipment/head_rig.jpg";
// Every equipment photo by file name, so a SHEET_EQUIPMENT card (constants.js) names
// its image as data: drop the file in src/assets/equipment/ and set `image`.
const EQUIP_IMAGES = Object.fromEntries(
  Object.entries(import.meta.glob("../../../assets/equipment/*.{jpg,jpeg,png,webp}", { eager: true, import: "default" }))
    .map(([path, url]) => [path.split("/").pop(), url])
);

/*
  QuotePrintable — the client-facing quote document (single source of PDF layout).
  Ported from legacy nyc-suite QuotePrintable + PrintPage, in the nycsuite.io look.
  Rendered into QuotePdfPreview for on-screen preview + html2pdf export.
  Brand: official horizontal black lockup, newyorkcapture.com, no red accents.
*/

const ADDRESS = ["62 Flushing Ave", "Brooklyn, NY 11205"];

function tierRange(t) {
  if (t.min === t.max) return String(t.min);
  if (!Number.isFinite(t.max)) return `${t.min}+`;
  return `${t.min}–${t.max}`;
}

// The Capture Date or Capture Schedule: lib/quoteSchedule.js, the one reader of the schedule.
const scheduleLabel = readScheduleLabel;

function Letterhead({ marginBottom = 40 }) {
  return (
    <div style={{ ...S.letterhead, marginBottom }}>
      <img src={logoBlack} alt="New York Capture" style={S.logo} />
      <div style={S.address}>
        {ADDRESS.map((l, i) => <div key={i}>{l}</div>)}
        <div style={S.addressMuted}>www.newyorkcapture.com</div>
      </div>
    </div>
  );
}

function TitleBlock({ title, subtitle, rightLabel, subtitleMark }) {
  return (
    <div style={S.titleBar}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={S.title}>{title}</div>
        {rightLabel && <div style={S.qid}>{rightLabel}</div>}
      </div>
      {subtitle && <div {...subtitleMark} style={S.subtitle}>{subtitle}</div>}
    </div>
  );
}

function Confidentiality() {
  return <div style={S.conf}>This document is confidential. Please do not share outside of your respective organization.</div>;
}

// Footer right side is a per-sheet label ("Camera Array Rate Sheet · 2 of 3"), not a
// global page number: the main quote page is variable-height and can flow onto extra
// physical pages, so absolute numbering desyncs the moment a quote runs long
// (Codex 2026-07-02). Sheet-scoped labels are immune to physical drift.
function Footer({ right }) {
  return (
    <div style={S.footer}>
      <span>New York Capture LLC &middot; newyorkcapture.com</span>
      {right && <span>{right}</span>}
    </div>
  );
}

// `marks`: the AI box reads the page section by section through data-box-section. Off
// (the default) nothing is emitted, so an existing quote prints byte for byte.
export default function QuotePrintable({ quote, imageSrc, marks = false }) {
  const q = quote || {};
  const mark = (name) => (marks ? { "data-box-section": name } : {});
  const docType = q.docType || "quote";
  const docLabel = DOC_TYPES[docType]?.label || "Quote";
  const isEstimate = docType === "estimate";
  const lineItems = q.lineItems || [];
  const qd = q.quoteDiscounts || [];

  const totals = useMemo(() => calcTotals(lineItems, qd), [lineItems, qd]);
  // Discounts as printed, in whole cents that add up with the Subtotal and Total.
  const shown = useMemo(() => shownDiscountAmounts(lineItems, qd), [lineItems, qd]);
  // lineItems is a real input: the lowpoly branch keys off catalog ids.
  // printWording is a real input everywhere the page's own sentences are built.
  const deliverables = useMemo(() => buildDeliverablesText(q), [q.sections, q.deliverableText, q.lineItems, q.printWording]);
  // deposit/lineItems/quoteDiscounts are real inputs now: a percent deposit
  // computes its dollar figure from the discounted total.
  // billing is an input too: pro bono drops the payment sentence.
  const terms = useMemo(() => buildTermsText(q), [q.sections, q.requiresDeposit, q.deposit, q.lineItems, q.quoteDiscounts, q.billing, q.printWording]);
  const policies = useMemo(() => buildPoliciesText(q), [q.sections, q.printWording]);

  // LiDAR rate-sheet addendum: auto-attaches when the quote carries a LiDAR line item.
  // v2 lines (lidar.model stamp) print the LIDAR_MODEL sheet the estimator prices from;
  // unstamped archived lines keep the frozen v1 matrix sheet (see LidarRateSheet).
  const showLidarSheet = hasLidarLineItem(q.lineItems);
  // Drone rate-sheet addendum (2026-09-04): attaches when the quote carries a drone line,
  // after the LiDAR sheet when both exist. Reads DRONE_MODEL (see DroneRateSheet).
  const showDroneSheet = hasDroneLineItem(q.lineItems);

  // Reference-image addendum: ONE included image per page. For PDF export the parent
  // passes `imageSrc` — a map of attachment id → base64 dataURL resolved BEFORE html2pdf
  // runs (F15), so html2canvas never loads a cross-origin Storage URL (which it drops).
  // On-screen preview passes no map and falls back to the in-session dataUrl, then the
  // durable Storage url for reloaded quotes.
  const refImages = addendumImages(q.attachments)
    .map(a => ({ att: a, src: (imageSrc && imageSrc[a.id]) || a.dataUrl || a.url || null }))
    .filter(x => x.src);
  // F16: one image per addendum page. The old 2-up layout overflowed a Letter page (page
  // header + two ~340px cards), and since each card is data-keep-together the 2nd couldn't
  // split → html2pdf pushed it to a fresh page, leaving a blank gap/page. One card per page
  // can't overflow, so no card is ever pushed and no blank page is inserted — and the
  // per-page footer + sequential page numbers stay correct (one card = one page).
  const addendumPages = [];
  for (let i = 0; i < refImages.length; i += 1) addendumPages.push(refImages.slice(i, i + 1));
  // The rule lives in builderHelpers, so the box's preview says what this page does.
  const showArraySheet = showsArraySheet(q);


  return (
    <>
      {/* ── Main quote page ── */}
      <div data-quote-page style={S.page}>
        <Confidentiality />
        <Letterhead />
        <TitleBlock title={docLabel} subtitle={`Prepared for ${q.contactName || "—"} · ${q.clientName || "—"}`} rightLabel={q.id} subtitleMark={mark("meta")} />

        <div {...mark("meta")} style={S.detailGrid}>
          <div><div style={S.ml}>Client</div><div style={S.mvBig}>{q.clientName || "—"}</div></div>
          <div><div style={S.ml}>Location</div><div style={S.mv}>{q.jobLocation || "TBD"}</div></div>
          <div><div style={S.ml}>{q.multiDay ? "Capture Schedule" : "Capture Date"}</div><div style={S.mv}>{scheduleLabel(q)}</div></div>
          <div><div style={S.ml}>Quote Date</div><div style={S.mv}>{q.quoteDate ? fmtDate(q.quoteDate) : "—"}</div></div>
        </div>

        {q.summary && (
          <div {...mark("summary")} style={S.summary}><div style={S.ml}>Summary</div>{q.summary}</div>
        )}

        <table {...mark("lines")} style={S.table}>
          <thead>
            <tr><td colSpan={4} style={S.tableBar}>3D Capture {docLabel}</td></tr>
            <tr style={{ borderBottom: "1px solid #C8C8C8" }}>
              {["Description", "QTY.", "Unit Rate", "Total"].map((h, i) => (
                <th key={h} style={{ ...S.th, textAlign: i === 0 ? "left" : i === 3 ? "right" : "center", width: i === 0 ? "50%" : "auto" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              const row = (item, idx) => {
                const lineDisc = calcLineDiscount(item);
                const hasQty = item.qty != null && item.qty > 0;
                // Gross line total (qty × rate) so the column sums to the Subtotal; the
                // discount note beneath + the totals block carry the savings.
                const lineTotal = (hasQty ? item.qty : 0) * (Number(item.rate) || 0);
                return (
                  <tr key={item.key ?? idx} style={S.tr}>
                    <td style={S.td}>
                      {item.description}
                      {item.discount && item.discount.value > 0 ? (
                        <div style={{ marginTop: 3 }}>
                          <span style={S.discBadge}>{item.discount.name || "Discount"}</span>
                          <span style={S.discNote}>{item.discount.type === "%" ? `${item.discount.value}% off` : `${fmtMoney(item.discount.value)} off ea.`} &middot; saves {fmtMoney(lineDisc)}</span>
                        </div>
                      ) : item.notes ? <div style={S.itemNote}>{item.notes}</div> : null}
                    </td>
                    <td style={{ ...S.td, textAlign: "center" }}>{hasQty ? item.qty : "n/a"}</td>
                    <td style={{ ...S.td, textAlign: "center", ...S.mono }}>{fmtMoney(item.rate, { maxDecimals: 4 })}</td>
                    <td style={{ ...S.td, textAlign: "right", ...S.mono }}>{hasQty ? fmtMoney(lineTotal) : "n/a"}</td>
                  </tr>
                );
              };
              // Line groups (2026-09-03): when any line carries a group, print each group
              // under a heading (label, optional note) with its subtotal, in the order of the
              // quote's block list (derived from the brief + the lines for quotes that predate
              // it); ungrouped lines last with no heading. A block with no lines does not
              // print. Otherwise the flat table as ever.
              const lineGroups = quoteLineGroups(q);
              const groups = groupLineItems(lineItems, lineGroupNotesFrom(lineGroups), lineGroups.map((g) => g.label));
              const printLabels = Object.fromEntries(lineGroups.map((g) => [g.label, g.printLabel]));
              if (!groups.some((g) => g.label)) return lineItems.map((item, idx) => row(item, idx));
              return groups.map((g) => (
                <React.Fragment key={g.label || "__ungrouped"}>
                  {g.label && (
                    <tr data-keep-together>
                      <td colSpan={4} style={S.groupHead}><span style={S.groupHeadLabel}>{printLabels[g.label] || g.label}</span>{g.note ? <span style={S.groupHeadNote}>{g.note}</span> : null}</td>
                    </tr>
                  )}
                  {g.items.map(({ item, idx }) => row(item, idx))}
                  {g.label && (
                    <tr>
                      <td colSpan={3} style={S.groupSubLabel}>{printLabels[g.label] || g.label} subtotal</td>
                      <td style={S.groupSubAmt}>{fmtMoney(g.subtotal)}</td>
                    </tr>
                  )}
                </React.Fragment>
              ));
            })()}
          </tbody>
        </table>

        <div data-keep-together {...mark("totals")} style={S.totals}>
          {totals.totalSavings > 0 && (
            <>
              <div style={S.totSub}>Subtotal: {fmtMoney(Math.round(totals.grossSubtotal * 100) / 100)}</div>
              {totals.lineDiscountTotal > 0 && (
                <div style={S.totDisc}>Line item discounts: {fmtMoney(-shown.line)}</div>
              )}
              {qd.map((d, i) => (
                <div key={i} style={S.totDisc}>{quoteDiscountRow(d, shown.quote[i])}</div>
              ))}
            </>
          )}
          <div style={S.totGrand}>{isEstimate ? "Estimated Total" : "Total"}: {fmtMoney(totals.total)}</div>
          {isEstimate && wordingOr(q, "estimateNote", ESTIMATE_NOTE) && <div style={S.estNote}>{wordingOr(q, "estimateNote", ESTIMATE_NOTE)}</div>}
          {/* Pro bono (2026-09-11): the lines, the subtotal and the Pro bono discount
              print as they do today; this one line says why the total is $0. */}
          {proBonoPrints(q) && wordingOr(q, "proBonoNote", PRO_BONO_PRINT_NOTE) && <div data-testid="qp-pro-bono-note" style={S.proBonoNote}>{wordingOr(q, "proBonoNote", PRO_BONO_PRINT_NOTE)}</div>}
        </div>

        <div data-keep-together {...mark("deliverables")} style={S.section}>
          <div style={S.sectionHead}>Deliverables</div>
          {deliverables.map((t, i) => <div key={i} style={S.body}>{t}</div>)}
          {/* Turnaround lines come from turnaroundLines(): the section checkbox gates the
              block, the generic raw-models sentence hides on LiDAR-only quotes, and the
              LiDAR line prints whenever a LiDAR processing line exists. */}
          {turnaroundLines(q).map((t, i) => <div key={`ta${i}`} style={S.body}>{t}</div>)}
        </div>

        <div data-keep-together {...mark("terms")} style={S.sectionThin}>
          <div style={S.sectionHead}>Terms</div>
          {terms.map((t, i) => <div key={i} style={{ ...S.body, fontSize: 13, color: "#333" }}>{t}</div>)}
        </div>

        {policies.length > 0 && (
          <div data-keep-together {...mark("policies")} style={S.sectionThin}>
            {policies.map((t, i) => <div key={i} style={S.policy}>{t}</div>)}
          </div>
        )}

        <Footer />
      </div>

      {/* ── Optional Camera Array rate-sheet addendum (3 pages) ── */}
      {showArraySheet && <ArrayRateSheet q={q} mark={mark} />}

      {/* ── Optional LiDAR rate-sheet addendum (3 pages) ── */}
      {showLidarSheet && <LidarRateSheet q={q} mark={mark} />}

      {/* ── Optional drone rate-sheet addendum (1 page) ── */}
      {showDroneSheet && <DroneRateSheet q={q} mark={mark} />}

      {/* ── Reference images addendum (one per page — F16, avoids overflow/blank pages) ── */}
      {addendumPages.map((pageImgs, pi) => (
        <div key={`addendum-${pi}`} data-quote-page style={{ ...S.page, height: 1056, display: "flex", flexDirection: "column" }}>
          {pi === 0 ? (
            <>
              <Confidentiality />
              <Letterhead marginBottom={32} />
              <TitleBlock title="Reference Images" subtitle={`Prepared for ${q.contactName || "—"} · ${q.clientName || "—"}`} />
            </>
          ) : (
            <div style={{ ...S.ml, marginBottom: 18 }}>Reference Images (cont.)</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {pageImgs.map(({ att, src }) => (
              <div key={att.id} data-keep-together style={S.refCard}>
                <div style={S.refImgWrap}>
                  <img src={src} alt={att.caption || att.name || "Reference image"} style={S.refImg} />
                </div>
                <div {...mark("image")} style={S.refCaption}>{att.caption || att.name || "Reference image"}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "auto" }}><Footer right={`Reference Images · ${pi + 1} of ${addendumPages.length}`} /></div>
        </div>
      ))}
    </>
  );
}

// LiDAR rate-sheet addendum (pricing + equipment), all numbers data-driven off the model
// that priced the line (LIDAR_MODEL for v2, frozen LIDAR_MATRIX for archived v1) so the
// sheet and the priced line item can never disagree. Facts only; cream/gold headers
// (photogrammetry = gold), no black hero bands.
// Shared chrome for rate-sheet addendum pages. Each page is EXACTLY one letter page
// tall (1056px at 96dpi) with the footer pinned to the bottom. Pagination is owned by
// src/lib/pdfExport.js (one div = one PDF page) — no CSS page breaks, no html2pdf
// spacer math, no browser-dependent drift (Freefall 2026-07-02).
function SheetPage({ children, right }) {
  return (
    <div data-quote-page style={{ ...S.page, height: 1056, display: "flex", flexDirection: "column" }}>
      <Confidentiality />
      <Letterhead marginBottom={24} />
      {children}
      <div style={{ marginTop: "auto" }}><Footer right={right} /></div>
    </div>
  );
}

// Equipment card shared by both rate sheets (image + specs, one keep-together block).
function EquipCard({ img, model, type, why, specs }) {
  return (
    <div data-keep-together style={S.equip}>
      <div style={S.equipImgWrap}><img src={img} alt={model} style={S.equipImg} /></div>
      <div style={{ flex: 1 }}>
        <div style={S.equipName}>{model}</div>
        {type && <div style={S.equipType}>{type}</div>}
        {why && <div style={S.equipWhy}>{why}</div>}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {Object.entries(specs).map(([k, v]) => (
              <tr key={k}><td style={S.specK}>{k}</td><td style={S.specV}>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Camera Array Rate Sheet addendum — 3 letter-fit pages, mirrors the approved
// nyc_website/bigrig-rate-sheet.html. Canonical record: docs/pricing/big-rig-pricing.md.
// Copy rules: no em dashes, no editorial fragments, complete factual sentences.
function ArrayRateSheet({ q, mark = () => ({}) }) {
  const preparedFor = `Prepared for ${q.contactName || "—"} · ${q.clientName || "—"}`;
  const byId = (list, id) => list.find(x => x.id === id) || {};
  const halfDay = byId(RATE_CATALOG.sessions, "half_art");
  const fullDay = byId(RATE_CATALOG.sessions, "full_art");
  const onLoc = byId(RATE_CATALOG.sessions, "full_onlocation");
  const truck = byId(RATE_CATALOG.addons, "truck");
  const cols = (VOLUME_TIERS.head_scan || []).map(tierRange);

  return (
    <>
      {/* Page 1 of 3 — base rates */}
      <SheetPage right="Camera Array Rate Sheet · 1 of 3">
        <TitleBlock title="Camera Array Rate Sheet" subtitle={preparedFor} />
        <div style={S.lsIntro}>
          Big Rig (175-camera array) · Head Rig (144-camera array). This sheet covers capture on both arrays, in-studio and on location. One standard rate applies to every industry.
        </div>

        <div style={S.lsSecTitle}>Base rates</div>
        <div data-keep-together>
          <div style={S.mxLabel}>In-Studio <span style={S.mxLabelSub}>· 62 Flushing Ave, Brooklyn</span></div>
          <table style={S.rt}>
            <tbody>
              <tr>
                <th style={S.rtRowLabel}>Partial day <span style={S.rowSub}>up to 2 hrs</span></th>
                <td style={S.rtCell}>{fmtMoney(halfDay.rate)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Full day <span style={S.rowSub}>8 hrs · 4 processed model minimum</span></th>
                <td style={S.rtCell}>{fmtMoney(fullDay.rate)}</td>
              </tr>
            </tbody>
          </table>
          <div style={S.lsOt}>Scans are billed per capture. Per-model rates are on the following page.</div>
        </div>

        <div data-keep-together style={{ marginTop: 18 }}>
          <div style={{ ...S.mxLabel, ...S.mxLabelGold }}>On-location</div>
          <table style={S.rt}>
            <tbody>
              <tr>
                <th style={S.rtRowLabel}>Day 1 <span style={S.rowSub}>~3 hr setup · 8 hrs capture · ~1.5 hr strike, same day</span></th>
                <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(onLoc.rate)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Consecutive days <span style={S.rowSub}>each additional back-to-back day</span></th>
                <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(ARRAY_SHEET.consecutiveDayRate)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Overtime <span style={S.rowSub}>each hr past 8 capture hrs</span></th>
                <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(ARRAY_SHEET.overtimeHourRate)}</td>
              </tr>
            </tbody>
          </table>
          <div style={S.lsOt}>All captures made during the day are included. Overtime counts <span style={S.mono2}>active capture time</span> only. Setup and strike do not count toward the 8 hours.</div>
        </div>
      </SheetPage>

      {/* Page 2 of 3 — per-model rates, previews, transport */}
      <SheetPage right="Camera Array Rate Sheet · 2 of 3">
        <TitleBlock title="Camera Array Rate Sheet" subtitle={preparedFor} />

        <div data-keep-together>
          <div style={S.lsSecTitle}>Per-model rates</div>
          <div style={S.lsSecDesc}>Each model is delivered captured and processed, with its model preview. In studio, models are billed alongside the session fee. On location, only the captures processed into models are billed. Volume pricing applies per scan type.</div>
          <table style={S.rt}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...S.mxHead, textAlign: "left", verticalAlign: "bottom" }}>Scan type</th>
                <th colSpan={cols.length} style={{ ...S.mxHead, ...S.mxHeadGold, borderBottom: "1px solid #E4DAC2" }}>Quantity</th>
              </tr>
              <tr>
                {cols.map(c => <th key={c} style={S.mxHead}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {[["Head", VOLUME_TIERS.head_scan], ["Full body", VOLUME_TIERS.body_scan]].map(([label, tiers]) => (
                <tr key={label}>
                  <th style={S.mxRowLabel}>{label}</th>
                  {tiers.map((t, i) => <td key={i} style={S.mxCell}>{fmtMoney(t.rate)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div data-keep-together style={{ marginTop: 16 }}>
          <div style={S.lsSecTitle}>Previews</div>
          <div style={S.lsSecDesc}>Raw-capture previews (USDZ / lo-res OBJ) are previews of captures that have not been processed into models.</div>
          <table style={S.rt}>
            <tbody>
              <tr>
                <th style={S.rtRowLabel}>Raw-capture previews <span style={S.rowSub}>per capture</span></th>
                <td style={S.rtCell}>{fmtMoney(ARRAY_SHEET.rawCapturePreviewRate)}</td>
              </tr>
            </tbody>
          </table>
          <div style={S.lsOt}>Raw-capture previews apply when a session runs more than 5 captures with a single finished model purchased. Film/TV productions are excluded.</div>
        </div>


        <div {...mark("sheet-note")} style={S.lsFoot}>{ARRAY_SHEET_FOOT}</div>
      </SheetPage>

      {/* Page 3 of 3 — equipment */}
      <SheetPage right="Camera Array Rate Sheet · 3 of 3">
        <TitleBlock title="Equipment" subtitle="Photogrammetry camera arrays, designed and built in-house" />
        <EquipCard img={bigRigImg} model={ARRAY_SHEET.equipment.bigRig.model} type={ARRAY_SHEET.equipment.bigRig.tag} why={ARRAY_SHEET.equipment.bigRig.why} specs={ARRAY_SHEET.equipment.bigRig.specs} />
        <EquipCard img={headRigImg} model={ARRAY_SHEET.equipment.headRig.model} type={ARRAY_SHEET.equipment.headRig.tag} why={ARRAY_SHEET.equipment.headRig.why} specs={ARRAY_SHEET.equipment.headRig.specs} />
        <div data-keep-together style={{ marginTop: 16 }}>
          <div style={S.lsSecTitle}>Transport &amp; travel</div>
          <div style={S.lsSecDesc}>All film and TV jobs require production transport through teamsters. The rig travels in a cube truck with a lift gate, 20 feet minimum. If the production cannot provide transport, a travel fee applies.</div>
          <table style={S.rt}>
            <tbody>
              <tr>
                <th style={S.rtRowLabel}>Travel fee <span style={S.rowSub}>when transport is not provided · adjusted for further locations</span></th>
                <td style={S.rtCell}>from {fmtMoney(truck.rate)}</td>
              </tr>
            </tbody>
          </table>
          <div style={S.lsOt}>Crew travel is calculated per job and included in your estimate.</div>
        </div>
      </SheetPage>
    </>
  );
}

// LiDAR Rate Sheet addendum — 3 letter-fit pages, mirrors the approved
// nyc_website/lidar-rate-sheet.html. Canonical record: docs/pricing/lidar-pricing.md.
// Capture only is INSURANCE: captured and archived, nothing is delivered without
// processing. Never describe it as "raw scans delivered".
// Dispatch on the model that priced the quote's LiDAR lines. Lines stamped lidar.model
// = "v2" print the v2 sheet; unstamped lines were priced on the v1 tier matrix and keep
// printing it, so a re-exported PDF of a quote a client already received never shows
// rates that disagree with its priced line (frozen by Ivin, 2026-09-03).
function LidarRateSheet({ q, mark = () => ({}) }) {
  return lidarModelVersion(q.lineItems) === "v2" ? <LidarRateSheetV2 q={q} mark={mark} /> : <LidarRateSheetV1 q={q} mark={mark} />;
}

// v2 (2026-09-03): crew-scaled capture day + per-setup processing + rush. Every number
// is read from LIDAR_MODEL so the sheet and the priced line can never disagree. Facts
// only, complete sentences, no em dashes (client-docs rules). A rates page, then the
// equipment page(s), which list only the gear the quote uses.
function LidarRateSheetV2({ q, mark = () => ({}) }) {
  const M = LIDAR_MODEL;
  const preparedFor = `Prepared for ${q.contactName || "—"} · ${q.clientName || "—"}`;
  // The turnaround note agrees with the Deliverables block (turnaroundLines): the same
  // day count, "from receipt of the scans" when every processing line was priced on
  // scans already in hand, and no turnaround at all on a capture-only quote.
  const procSource = lidarProcessingOnly(q.lineItems) ? "receipt of the scans" : "capture";
  const procNote = lidarSheetNote(q.lineItems);
  const rushPct = Math.round(M.rush.processingMultiplier * 100);
  const rushDays = M.rush.turnaroundDays;
  const goldHead = { ...S.mxHead, ...S.mxHeadGold, textAlign: "right" };
  // Equipment page(s): only the gear this quote uses (lidarSheetEquipment), two cards a
  // page so a page never overflows its letter height. A card whose image file is missing
  // is left off rather than printed without a photo.
  const cards = lidarSheetEquipment(q).filter((c) => EQUIP_IMAGES[c.image]);
  const equipPages = [];
  for (let i = 0; i < cards.length; i += 2) equipPages.push(cards.slice(i, i + 2));
  const pageCount = 1 + equipPages.length;

  return (
    <>
      {/* Page 1 — rates */}
      <SheetPage right={`LiDAR Rate Sheet · 1 of ${pageCount}`}>
        <TitleBlock title="LiDAR Rate Sheet" subtitle={preparedFor} />

        <div style={S.lsIntro}>
          Terrestrial LiDAR · Leica RTC 360. Every LiDAR job is billed as a <b>capture day</b> plus <b>processing</b> of the scan set. A setup is one scan position: a full 360° scan plus a 432 MP spherical HDR image. Capture is priced by crew and day length. Processing is priced by the number of setups.
        </div>

        <div data-keep-together>
          <div style={S.lsSecTitle}>Capture</div>
          <div style={S.lsSecDesc}>Lead operator with the RTC 360. A full day is {M.capture.dayHours.standard} hours, or {M.capture.dayHours.film} hours for film and television productions.</div>
          <table style={S.rt}>
            <tbody>
              <tr>
                <th style={S.rtRowLabel}>Full day <span style={S.rowSub}>{M.capture.dayHours.standard} hrs · {M.capture.dayHours.film} hrs for film and TV</span></th>
                <td style={S.rtCell}>{fmtMoney(M.capture.baseDay)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Partial day <span style={S.rowSub}>up to {M.capture.partialHours} hrs</span></th>
                <td style={S.rtCell}>{fmtMoney(M.capture.partialDay)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Each additional crew member <span style={S.rowSub}>per day</span></th>
                <td style={S.rtCell}>{fmtMoney(M.capture.perAdditionalCrew)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Overtime <span style={S.rowSub}>per crew member, each hour past the day</span></th>
                <td style={S.rtCell}>{fmtMoney(M.capture.overtimePerCrewHour)}</td>
              </tr>
            </tbody>
          </table>
          <div style={S.lsOt}>Photogrammetry adds a second crew member with a Canon R5 for a high-resolution texture pass.</div>
        </div>

        <div data-keep-together style={{ marginTop: 18 }}>
          <div style={S.lsSecTitle}>Processing</div>
          <div style={S.lsSecDesc}>Registered point cloud (PTX or E57) and a textured OBJ. The OBJ is textured from the spherical HDR for LiDAR only, or from the Canon R5 photos when photogrammetry is added.</div>
          <table style={S.rt}>
            <thead>
              <tr>
                <th style={{ ...S.mxHead, textAlign: "left" }}>Scan set</th>
                <th style={{ ...S.mxHead, textAlign: "right" }}>LiDAR only</th>
                <th style={goldHead}>+ Photogrammetry</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th style={S.rtRowLabel}>Per setup</th>
                <td style={S.rtCell}>{fmtMoney(M.processing.perSetup.lidar)}</td>
                <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(M.processing.perSetup.photo)}</td>
              </tr>
              <tr>
                <th style={S.rtRowLabel}>Minimum per job</th>
                <td style={S.rtCell}>{fmtMoney(M.processing.minimum.lidar)}</td>
                <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(M.processing.minimum.photo)}</td>
              </tr>
            </tbody>
          </table>
          <div {...mark("sheet-note")} style={S.lsOt}>{procNote}</div>
        </div>

        <div data-keep-together style={S.lsNote}><b>High-density scans.</b> When high-density scans are requested, processing is billed at {M.highDensity.processingMultiplier} times the standard rate. The capture day is unchanged.</div>

        <div data-keep-together style={S.lsNote}><b>Rush processing.</b> Processing begins immediately after {procSource} and is delivered within {rushDays} business day{rushDays === 1 ? "" : "s"}. Billed at {rushPct}% of the processing fee{M.rush.minimum > 0 ? `, ${fmtMoney(M.rush.minimum)} minimum` : ""}.</div>

        <div data-keep-together style={S.lsNote2}><b>Capture only.</b> A capture-only day is billed at the capture rate alone. The site is captured and the data is archived, scans and photos alike. No data is delivered without processing. Processing can be added later at the rates above.</div>

        <div style={S.lsFoot}>Equipment specifications on the following page{equipPages.length > 1 ? "s" : ""}.</div>
      </SheetPage>

      {/* Equipment pages */}
      {equipPages.map((page, i) => (
        <SheetPage key={i} right={`LiDAR Rate Sheet · ${i + 2} of ${pageCount}`}>
          <TitleBlock title="Equipment" subtitle="Used for LiDAR capture" />
          {page.map((c) => <EquipCard key={c.key} img={EQUIP_IMAGES[c.image]} model={c.model} type={c.type} specs={c.specs} />)}
        </SheetPage>
      ))}
    </>
  );
}

// v1 (2026-07-01 to 2026-09-03): the retired tier matrix. Rendered ONLY for archived
// quotes whose LiDAR lines carry no model stamp. Reads the frozen LIDAR_MATRIX.
function LidarRateSheetV1({ q, mark = () => ({}) }) {
  const M = LIDAR_MATRIX;
  const preparedFor = `Prepared for ${q.contactName || "—"} · ${q.clientName || "—"}`;
  const bands = [["0–3 hours", "partial"], ["3–8 hours", "full"]];
  const tiers = M.setupTiers;
  const tierHead = { "<=25": "≤ 25", "26-60": "26–60", "61-99": "61–99", "100+": "100+" };

  const MatrixTable = ({ texture, gold }) => (
    <table style={S.rt}>
      <thead>
        <tr>
          <th rowSpan={2} style={{ ...S.mxHead, ...(gold ? S.mxHeadGold : {}), textAlign: "left", verticalAlign: "bottom" }}>Time on site</th>
          <th colSpan={4} style={{ ...S.mxHead, ...(gold ? S.mxHeadGold : {}), borderBottom: "1px solid #E4DAC2" }}>Number of setups</th>
        </tr>
        <tr>
          {tiers.map(t => <th key={t} style={{ ...S.mxHead, ...(gold ? S.mxHeadGold : {}) }}>{tierHead[t]}</th>)}
        </tr>
      </thead>
      <tbody>
        {bands.map(([label, band]) => (
          <tr key={band}>
            <th style={S.mxRowLabel}>{label}</th>
            {tiers.map(t => {
              const cell = M.processing[band][t];
              const val = cell ? cell[texture] : null;
              return <td key={t} style={{ ...S.mxCell, ...(gold ? S.mxCellGold : {}), ...(val == null ? S.mxNa : {}) }}>{val == null ? "—" : fmtMoney(val)}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      {/* Page 1 of 3 — capture only */}
      <SheetPage right="LiDAR Rate Sheet · 1 of 3">
        <TitleBlock title="LiDAR Rate Sheet" subtitle={preparedFor} />

        <div style={S.lsIntro}>
          Terrestrial LiDAR · Leica RTC 360. Priced by <b>time on site</b> and <b>number of setups</b> (scan positions); each setup is a full 360° scan plus a 432 MP spherical HDR image. Billed as <b>capture only</b> (captured and archived) or <b>capture + processing</b> (registered point cloud + textured OBJ).
        </div>

        <div data-keep-together>
          <div style={S.lsSecTitle}>Capture only</div>
          <div style={S.lsSecDesc}>Capture only is insurance. The site is captured and the data is archived, scans and photos alike. <b>No data is delivered without processing.</b> Processing can be added later, billed as the difference between the capture-only and capture + processing rates.</div>
          <table style={S.rt}>
            <thead>
              <tr>
                <th style={{ ...S.mxHead, textAlign: "left" }}>Day length</th>
                <th style={{ ...S.mxHead, textAlign: "right" }}>LiDAR only</th>
                <th style={{ ...S.mxHead, ...S.mxHeadGold, textAlign: "right" }}>+ Photogrammetry</th>
              </tr>
            </thead>
            <tbody>
              {[["Partial day (0–3 hrs)", M.captureOnly.partial], ["Full day (3–8 hrs)", M.captureOnly.full], ["Overtime (per hr past 8)", M.captureOnly.overtime]].map(([label, cell]) => (
                <tr key={label}>
                  <th style={S.rtRowLabel}>{label}</th>
                  <td style={S.rtCell}>{fmtMoney(cell.lidar)}</td>
                  <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(cell.photo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SheetPage>

      {/* Page 2 of 3 — capture + processing */}
      <SheetPage right="LiDAR Rate Sheet · 2 of 3">
        <TitleBlock title="LiDAR Rate Sheet" subtitle={preparedFor} />

        <div style={S.lsSecTitle}>Capture + processing</div>
        <div style={S.lsSecDesc}>Registered point cloud (PTX / E57) + textured OBJ. The OBJ is textured from the spherical HDR for LiDAR only, or from the Canon R5 photos when photogrammetry is added.</div>
        <div {...mark("sheet-note")} style={S.lsProcNote}>{lidarSheetNoteV1(q.lineItems)}</div>

        <div data-keep-together>
          <div style={S.mxLabel}>LiDAR only</div>
          <MatrixTable texture="lidar" gold={false} />
        </div>
        <div data-keep-together>
          <div style={{ ...S.mxLabel, ...S.mxLabelGold }}>LiDAR + photogrammetry</div>
          <MatrixTable texture="photo" gold={true} />
        </div>
        <div style={S.lsOt}>Overtime past 8 hrs: <span style={S.mono2}>{fmtMoney(M.processing.overtime.lidar)}/hr</span> LiDAR only · <span style={S.mono2}>{fmtMoney(M.processing.overtime.photo)}/hr</span> with photogrammetry.</div>

        <div data-keep-together style={S.lsNote}><b>High-density capture · ×{M.highDensityMultiplier} whole day.</b> When 35%+ of scans are high-density, the entire day is billed at ×{M.highDensityMultiplier}, and overtime is billed at {M.highDensityMultiplier}× the standard overtime rate. High-density capture is recommended for large-volume spaces: large outdoor sites, or big interiors like halls and very large rooms.</div>

        <div style={S.lsFoot}>Equipment specifications on the following page.</div>
      </SheetPage>

      {/* Page 3 of 3 — equipment */}
      <SheetPage right="LiDAR Rate Sheet · 3 of 3">
        <TitleBlock title="Equipment" subtitle="Used for LiDAR capture" />
        <EquipCard img={rtcImg} model={M.equipment.rtc360.model} type={M.equipment.rtc360.type} specs={M.equipment.rtc360.specs} />
        <EquipCard img={r5Img} model={M.equipment.r5.model} type={M.equipment.r5.type} specs={M.equipment.r5.specs} />
      </SheetPage>
    </>
  );
}

// Drone Rate Sheet addendum (approved 2026-09-04), one letter page laid out like
// LidarRateSheetV2. Every number is read from DRONE_MODEL, and the permit line from
// RATE_CATALOG, so the sheet and the priced line can never disagree. Facts only,
// complete sentences, no em dashes (client-docs rules). Canonical record:
// docs/pricing/drone-pricing.md. There is no equipment page: the Mavic 3E specs have
// not been verified with Ivin and specs are never invented.
function DroneRateSheet({ q, mark = () => ({}) }) {
  const M = DRONE_MODEL;
  const preparedFor = `Prepared for ${q.contactName || "—"} · ${q.clientName || "—"}`;
  const rushPct = Math.round(M.rush.processingMultiplier * 100);
  const rushDays = M.rush.turnaroundDays;
  // Same contract as LidarRateSheetV2: the day count and wording the Deliverables block
  // prints ("from receipt of the stills" when processing only), or the capture-only
  // sentence when the quote has no drone processing line.
  const procSource = droneProcessingOnly(q.lineItems) ? "receipt of the stills" : "capture";
  const procNote = droneSheetNote(q.lineItems);
  const permit = (RATE_CATALOG.addons || []).find((a) => a.id === "drone_permit");
  const goldHead = { ...S.mxHead, ...S.mxHeadGold, textAlign: "right" };

  return (
    <SheetPage right="Drone Rate Sheet · 1 of 1">
      <TitleBlock title="Drone Rate Sheet" subtitle={preparedFor} />

      <div style={S.lsIntro}>
        Aerial photogrammetry · {M.aircraft}. Every drone job is billed as a <b>capture day</b> plus <b>processing</b> of the stills flown. Capture is priced by crew and day length. Processing is priced by the acres flown and the deliverable.
      </div>

      <div data-keep-together>
        <div style={S.lsSecTitle}>Capture</div>
        <div style={S.lsSecDesc}>FAA Part 107 remote pilot with the {M.aircraft}, batteries and controller. A full day is {M.capture.dayHours.standard} hours, or {M.capture.dayHours.film} hours for film and television productions.</div>
        <table style={S.rt}>
          <tbody>
            <tr>
              <th style={S.rtRowLabel}>Full day <span style={S.rowSub}>{M.capture.dayHours.standard} hrs · {M.capture.dayHours.film} hrs for film and TV</span></th>
              <td style={S.rtCell}>{fmtMoney(M.capture.baseDay)}</td>
            </tr>
            <tr>
              <th style={S.rtRowLabel}>Partial day <span style={S.rowSub}>up to {M.capture.partialHours} hrs</span></th>
              <td style={S.rtCell}>{fmtMoney(M.capture.partialDay)}</td>
            </tr>
            <tr>
              <th style={S.rtRowLabel}>Each additional crew member <span style={S.rowSub}>per day</span></th>
              <td style={S.rtCell}>{fmtMoney(M.capture.perAdditionalCrew)}</td>
            </tr>
            <tr>
              <th style={S.rtRowLabel}>Overtime <span style={S.rowSub}>per crew member, each hour past the day</span></th>
              <td style={S.rtCell}>{fmtMoney(M.capture.overtimePerCrewHour)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div data-keep-together style={{ marginTop: 14 }}>
        <div style={S.lsSecTitle}>Processing</div>
        <div style={S.lsSecDesc}>Photogrammetry from the drone stills, delivered raw. Geometry only is a scaled point cloud and mesh with no textures, for matchmove. Textured mesh adds the texture from the same stills.</div>
        <table style={S.rt}>
          <thead>
            <tr>
              <th style={{ ...S.mxHead, textAlign: "left" }}>Acres flown</th>
              <th style={{ ...S.mxHead, textAlign: "right" }}>Geometry only</th>
              <th style={goldHead}>Textured mesh</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th style={S.rtRowLabel}>Per acre</th>
              <td style={S.rtCell}>{fmtMoney(M.processing.perAcre.geometry)}</td>
              <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(M.processing.perAcre.textured)}</td>
            </tr>
            <tr>
              <th style={S.rtRowLabel}>Minimum per job</th>
              <td style={S.rtCell}>{fmtMoney(M.processing.minimum.geometry)}</td>
              <td style={{ ...S.rtCell, ...S.rtCellGold }}>{fmtMoney(M.processing.minimum.textured)}</td>
            </tr>
          </tbody>
        </table>
        <div {...mark("sheet-note")} style={S.lsOt}>{procNote} Orthomosaics and stills-only deliverables are quoted separately.</div>
      </div>

      <div data-keep-together style={S.lsNote}><b>Rush processing.</b> Processing begins immediately after {procSource} and is delivered within {rushDays} business day{rushDays === 1 ? "" : "s"}. Billed at {rushPct}% of the processing fee{M.rush.minimum > 0 ? `, ${fmtMoney(M.rush.minimum)} minimum` : ""}.</div>

      <div data-keep-together style={S.lsNote2}><b>Capture only.</b> A capture-only day is billed at the capture rate alone. The site is flown and the stills are archived. No data is delivered without processing. Processing can be added later at the rates above.</div>

      <div data-keep-together style={S.lsNote2}><b>Permit and insurance.</b> Production holds the permit and the drone liability insurance; New York Capture supplies the Part 107 pilot certificate and aircraft registration.{permit ? ` ${permit.label} is available as a separate line at ${fmtMoney(permit.rate)}.` : ""}</div>
    </SheetPage>
  );
}

const fontStack = "'DM Sans', system-ui, -apple-system, sans-serif";
const monoStack = "'DM Mono', 'SF Mono', monospace";

const S = {
  page: { width: 816, background: "#fff", color: "#000", padding: "48px 56px", fontFamily: fontStack, fontWeight: 300, fontSize: 14, lineHeight: 1.6, boxSizing: "border-box" },
  conf: { fontSize: 10, fontStyle: "italic", color: "#AAA", marginBottom: 20, textAlign: "center" },
  letterhead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  logo: { height: 30, width: "auto" },
  address: { textAlign: "right", fontSize: 11.5, color: "#333", lineHeight: 1.75 },
  addressMuted: { color: "#888" },
  titleBar: { borderBottom: "2px solid #111", paddingBottom: 14, marginBottom: 28 },
  title: { fontSize: 18, fontWeight: 400, letterSpacing: "0.02em" },
  qid: { fontSize: 11, color: "#888", fontFamily: monoStack },
  subtitle: { fontSize: 11, color: "#888", marginTop: 4 },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 40px", marginBottom: 28 },
  ml: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "#999", marginBottom: 3 },
  mv: { fontSize: 13 },
  mvBig: { fontSize: 14, fontWeight: 400 },
  summary: { marginBottom: 24, fontSize: 13.5, color: "#222", lineHeight: 1.55 },
  table: { width: "100%", borderCollapse: "collapse", marginBottom: 6 },
  tableBar: { background: "#111", padding: "7px 16px", color: "#fff", fontSize: 10, fontWeight: 400, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center" },
  th: { padding: "9px 16px", fontSize: 12, fontWeight: 300, color: "#444" },
  tr: { borderBottom: "1px solid #E5E5E5", verticalAlign: "top" },
  td: { padding: "9px 16px", fontSize: 13.5, color: "#222" },
  mono: { fontFamily: monoStack },
  itemNote: { fontSize: 12, color: "#666", marginTop: 2 },
  // Line-group heading + subtotal rows (2026-09-03). Quiet: a tinted band, the label in
  // sentence weight, the note small and gray, the subtotal in the mono money column.
  groupHead: { background: "#F4F2EE", padding: "7px 16px", borderBottom: "1px solid #DDD", verticalAlign: "top" },
  groupHeadLabel: { fontSize: 13, fontWeight: 500, color: "#111", marginRight: 10 },
  groupHeadNote: { fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#666" },
  groupSubLabel: { padding: "6px 16px 10px", fontSize: 12, color: "#666", textAlign: "right", borderBottom: "1px solid #C8C8C8" },
  groupSubAmt: { padding: "6px 16px 10px", fontSize: 12.5, color: "#111", fontWeight: 500, textAlign: "right", fontFamily: monoStack, borderBottom: "1px solid #C8C8C8" },
  discBadge: { display: "inline-block", padding: "1px 6px", borderRadius: 3, fontSize: 9, background: "#E8F5E9", color: "#2E7D32", marginRight: 6 },
  discNote: { fontSize: 10, color: "#2E7D32" },
  totals: { textAlign: "right", paddingRight: 16, margin: "14px 0 28px" },
  totSub: { fontSize: 12, color: "#888", marginBottom: 4 },
  totDisc: { fontSize: 12, color: "#2E7D32", marginBottom: 4 },
  totGrand: { borderTop: "2px solid #111", display: "inline-block", paddingTop: 6, marginTop: 4, fontWeight: 700, fontSize: 16 },
  estNote: { fontSize: 10, color: "#999", fontStyle: "italic", marginTop: 4 },
  proBonoNote: { fontSize: 11, color: "#555", marginTop: 6 },
  section: { borderTop: "1px solid #E0E0E0", paddingTop: 18, marginBottom: 16 },
  sectionThin: { borderTop: "1px solid #EFEFEF", paddingTop: 16, marginBottom: 16 },
  sectionHead: { fontWeight: 700, fontSize: 13, letterSpacing: "0.03em", marginBottom: 10 },
  body: { fontSize: 13.5, color: "#222", marginBottom: 6, lineHeight: 1.55 },
  policy: { fontSize: 12, color: "#555", lineHeight: 1.6, marginBottom: 6 },
  footer: { borderTop: "1px solid #E0E0E0", paddingTop: 12, marginTop: 20, display: "flex", justifyContent: "space-between", fontSize: 10, color: "#999" },
  refCard: { border: "1px solid #E0E0E0", borderRadius: 2, overflow: "hidden", background: "#FFF" },
  refImgWrap: { height: 300, background: "#F8F8F8", display: "flex", alignItems: "center", justifyContent: "center" },
  refImg: { maxHeight: 290, maxWidth: "100%", objectFit: "contain", display: "block" },
  refCaption: { padding: "10px 14px", fontSize: 12, color: "#333", borderTop: "1px solid #EEE" },
  // ── LiDAR rate-sheet addendum (cream/gold; gold = photogrammetry; no black bars) ──
  lsIntro: { fontSize: 13, color: "#333", lineHeight: 1.6, marginBottom: 22 },
  lsSecTitle: { fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 3 },
  lsSecDesc: { fontSize: 12.5, color: "#666", marginBottom: 12, lineHeight: 1.5 },
  lsProcNote: { fontSize: 12.5, color: "#333", fontWeight: 500, marginBottom: 12 },
  rt: { width: "100%", borderCollapse: "collapse", marginBottom: 4 },
  mxHead: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#444", background: "#F1EFEA", padding: "7px 12px", borderBottom: "2px solid #DDD", textAlign: "center" },
  mxHeadGold: { background: "#F3EAD4", color: "#7a5b1a" },
  mxRowLabel: { fontSize: 13, fontWeight: 500, color: "#1a1a1a", padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #ECECEC" },
  mxCell: { fontSize: 13, fontFamily: monoStack, color: "#111", fontWeight: 500, padding: "8px 8px", textAlign: "center", borderBottom: "1px solid #ECECEC" },
  mxCellGold: { color: "#9a7420" },
  mxNa: { color: "#CCC", fontWeight: 400 },
  mxLabel: { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#111", margin: "14px 0 6px" },
  mxLabelGold: { color: "#7a5b1a" },
  rtRowLabel: { fontSize: 13, fontWeight: 500, color: "#1a1a1a", padding: "8px 14px", textAlign: "left", borderBottom: "1px solid #ECECEC" },
  rtCell: { fontSize: 13.5, fontFamily: monoStack, color: "#333", padding: "8px 14px", textAlign: "right", borderBottom: "1px solid #ECECEC" },
  rtCellGold: { background: "#FAF5E9", color: "#111", fontWeight: 500 },
  lsOt: { fontSize: 12.5, color: "#555", marginTop: 8 },
  mono2: { fontFamily: monoStack, color: "#111" },
  lsNote: { fontSize: 12.5, color: "#333", background: "#F7F6F2", borderRadius: 6, padding: "10px 14px", marginTop: 12, lineHeight: 1.5 },
  lsNote2: { fontSize: 12.5, color: "#444", marginTop: 12, lineHeight: 1.5 },
  lsFoot: { fontSize: 11.5, color: "#888", lineHeight: 1.6, marginTop: 12, borderTop: "1px solid #ECECEC", paddingTop: 10 },
  equip: { display: "flex", gap: 24, padding: "10px 0", borderBottom: "1px solid #ECECEC" },
  equipImgWrap: { width: 260, flexShrink: 0, background: "#F4F3F0", borderRadius: 6, height: 200, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" },
  equipImg: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" },
  equipName: { fontSize: 17, fontWeight: 700, color: "#111" },
  equipType: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#888", margin: "5px 0 12px" },
  equipWhy: { fontSize: 11.5, color: "#555", lineHeight: 1.45, margin: "0 0 8px" },
  specK: { color: "#888", fontSize: 12, padding: "2px 0", borderBottom: "1px solid #F0F0F0" },
  specV: { textAlign: "right", fontFamily: monoStack, color: "#1a1a1a", fontSize: 12, padding: "2px 0", borderBottom: "1px solid #F0F0F0" },
  mxLabelSub: { fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#999" },
  rowSub: { color: "#999", fontWeight: 300, fontSize: 11.5, marginLeft: 5 },
};
